import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Circle, Trash, Flag } from "@phosphor-icons/react";

/**
 * Read-through modal for a milestone. Renders the title, due date, and status
 * plus quick actions (toggle complete, delete) so partners can act without
 * scanning the whole tab row.
 */
export default function MilestoneViewerDialog({ milestone, index, open, onOpenChange, onToggle, onDelete }) {
  if (!milestone) return null;
  const done = milestone.status === "done";
  const dueLabel = milestone.due_date
    ? new Date(milestone.due_date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "No due date set";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="milestone-viewer-dialog">
        <DialogHeader className="border-b border-primary/30 pb-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
            <Flag size={11} weight="fill" /> Milestone
            <span className={`ml-2 ${done ? "text-emerald-500" : "text-muted-foreground"}`}>
              {done ? "Complete" : "Pending"}
            </span>
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">{milestone.title}</DialogTitle>
        </DialogHeader>

        <section className="space-y-3">
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Due</div>
            <div className="font-serif text-sm">{dueLabel}</div>
          </div>
          {milestone.notes && (
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
              <div className="font-serif text-sm whitespace-pre-wrap">{milestone.notes}</div>
            </div>
          )}
        </section>

        <DialogFooter className="border-t border-border pt-4 gap-2 flex-wrap sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onDelete?.(index)}
            data-testid="milestone-viewer-delete"
            className="rounded-none text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash size={14} className="mr-1.5" /> Delete
          </Button>
          <Button
            onClick={() => onToggle?.(index)}
            data-testid="milestone-viewer-toggle"
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {done ? <><Circle size={14} className="mr-1.5" /> Reopen</> : <><CheckCircle size={14} weight="fill" className="mr-1.5" /> Mark complete</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
